import classNames from 'classnames';
import { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ChampionshipStageTab } from 'modules/championship/domain/enums/championship-stage-tab';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { isStageTabAvailable } from 'modules/championship/domain/utils/stage-tab-availability';
import { ChampionshipStateData } from 'modules/championship/store/championship';
import { useAcceptedChampionshipTeams } from 'modules/championship/ui/hooks/use-accepted-championship-teams';
import useCheckAcceptedTeams from 'modules/championship/ui/hooks/use-check-accepted-teams';
import { useSelectedStageFromRoute } from 'modules/championship/ui/hooks/use-selected-stage-from-route';
import { Pages } from 'modules/navigation/domain/enums/pages';
import StageMatchRoutes from './matches/stage-match-routes';
import StageMatches from './matches/stage-matches';
import StageGridRoutes from './stage-grid/stage-grid-routes';
import StageInfoForm from './stage-info-form';
import StageTabs, { useIsStageMatchRoute } from './stage-tabs';
import { StagesGridSkeleton } from './stages-grid-skeleton';
import TeamGroupsForm from './team-groups/team-groups-form';

type StageContentProps = {
  championship: ChampionshipStateData;
  stage: StageV2;
};

/**
 * Содержимое открытого этапа. Отдельный компонент, потому что его хуки нельзя
 * вызывать, пока не известно, что выбран именно этот этап: `useCheckAcceptedTeams`
 * читает выбранный этап из стора и иначе на переходном кадре вычистил бы команды
 * у предыдущего.
 */
const StageContent = ({ championship, stage }: StageContentProps) => {
  const { teams } = useAcceptedChampionshipTeams(championship.id);
  useCheckAcceptedTeams(championship.id);

  const isMatchRoute = useIsStageMatchRoute();
  const stageRoute = Pages.CHAMPIONSHIPS.STEPS.builders.stage(championship.id, stage.id);

  /** Прямой заход на недоступную подвкладку уводит на «Редактирование» — она есть всегда. */
  const requireTab = (tab: ChampionshipStageTab, element: ReactElement) =>
    isStageTabAvailable(stage, tab) ? element : <Navigate to={stageRoute} replace />;

  return (
    <>
      <div className="mb-5">
        <StageTabs championshipId={championship.id} stage={stage} />
      </div>
      <div className={classNames('w-full px-6', { 'flex h-full flex-col': isMatchRoute })}>
        <Routes>
          <Route
            path={ChampionshipStageTab.TEAMS}
            element={requireTab(ChampionshipStageTab.TEAMS, <TeamGroupsForm stage={stage} allTeams={teams || []} />)}
          />
          <Route
            path={`${ChampionshipStageTab.GRID}/*`}
            element={requireTab(ChampionshipStageTab.GRID, <StageGridRoutes stage={stage} />)}
          />
          <Route
            path={ChampionshipStageTab.MATCHES}
            element={requireTab(
              ChampionshipStageTab.MATCHES,
              <StageMatches stage={stage} championshipId={championship.id} tournamentId={championship.tournamentId} />,
            )}
          />
          <Route
            path={`${ChampionshipStageTab.MATCHES}/:gameId/*`}
            element={requireTab(
              ChampionshipStageTab.MATCHES,
              <StageMatchRoutes championshipId={championship.id} stage={stage} />,
            )}
          />
          <Route index element={<StageInfoForm championshipId={championship.id} stage={stage} />} />
          <Route path="*" element={<Navigate to={stageRoute} replace />} />
        </Routes>
      </div>
    </>
  );
};

type StageRoutesProps = {
  championship: ChampionshipStateData;
  isStagesReady: boolean;
};

/** Открытый этап: `/championships/:id/steps/:stageId/...`. */
const StageRoutes = ({ championship, isStagesReady }: StageRoutesProps) => {
  const { stage, isMissing, isReady } = useSelectedStageFromRoute(isStagesReady);

  if (isMissing) {
    return <Navigate to={Pages.CHAMPIONSHIPS.STEPS.builders.index(championship.id)} replace />;
  }

  if (!isReady || !stage) {
    return (
      <div className="w-full px-6">
        <StagesGridSkeleton />
      </div>
    );
  }

  return <StageContent championship={championship} stage={stage} />;
};

export default StageRoutes;
